import React, { useState, useRef } from "react";
import { isAuthenticated } from "../../auth";
import Vote from "./vote";
import { AddComments } from "../../auth/apiProduct";
import ViewVoteStarButton from "./ViewVoteStarButton";
import UserComment from "./userComment";

const ProductPreview = ({
  infoSource,
  ProductsViewload,
  comments,
  userInfo,
  productId,
  voteA,
  setShowComments,
  loadProductsView,
  ButtonAddComments,
  commentProduct,
  setUserVote,
  SetSizeComments,
  showComments,
  setCommentsPro,
  sizeComments,
  setSkip,
  skip,
  limit,
}) => {
  const divUserComment = useRef(null);
  const { token } = isAuthenticated();

  const [addComment, setAddComment] = useState(false);
  const [values, setValues] = useState({
    name: "",
    description: "",
    error: "",
  });
  // Was previously declared inside a nested "CommentForm" component function -- see the comment
  // above its old definition for why that caused a remount (and this state resetting) on every
  // keystroke. Lifted here alongside the form's other state, unchanged in behavior.
  const [focused, setFocused] = useState(null);

  const { name, description, error } = values;

  // Couleurs Elakay
  const colors = {
    primary: '#007399',
    primaryDark: '#004b63',
    primaryLight: '#00a4d8',
    accent: '#ef4444',
    background: '#f8fafc',
    white: '#ffffff',
    text: '#1e293b',
    textLight: '#64748b',
    border: '#e2e8f0',
  };

  // Styles épurés et professionnels
  const styles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '2rem 1rem',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    },
    formCard: {
      background: colors.white,
      borderRadius: '12px',
      border: `1px solid ${colors.border}`,
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
      overflow: 'hidden',
      marginTop: '2rem',
    },
    formHeader: {
      padding: '1.5rem 2rem',
      borderBottom: `1px solid ${colors.border}`,
      background: colors.background,
    },
    formTitle: {
      fontSize: '1.25rem',
      fontWeight: '600',
      color: colors.text,
      margin: 0,
    },
    formBody: {
      padding: '2rem',
    },
    inputGroup: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: '500',
      color: colors.text,
      marginBottom: '0.5rem',
    },
    input: {
      width: '100%',
      padding: '0.75rem 1rem',
      fontSize: '0.9375rem',
      border: `1px solid ${colors.border}`,
      borderRadius: '8px',
      outline: 'none',
      transition: 'all 0.2s ease',
      fontFamily: 'inherit',
      color: colors.text,
    },
    textarea: {
      width: '100%',
      padding: '0.75rem 1rem',
      fontSize: '0.9375rem',
      border: `1px solid ${colors.border}`,
      borderRadius: '8px',
      outline: 'none',
      transition: 'all 0.2s ease',
      fontFamily: 'inherit',
      color: colors.text,
      minHeight: '120px',
      resize: 'vertical',
      lineHeight: '1.6',
    },
    helperText: {
      fontSize: '0.8125rem',
      color: colors.textLight,
      marginTop: '0.375rem',
    },
    buttonGroup: {
      display: 'flex',
      gap: '0.75rem',
      justifyContent: 'flex-end',
      paddingTop: '1.5rem',
      borderTop: `1px solid ${colors.border}`,
      marginTop: '1.5rem',
    },
    button: {
      padding: '0.625rem 1.5rem',
      fontSize: '0.9375rem',
      fontWeight: '500',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontFamily: 'inherit',
    },
    primaryButton: {
      background: colors.primary,
      color: colors.white,
    },
    secondaryButton: {
      background: colors.white,
      color: colors.text,
      border: `1px solid ${colors.border}`,
    },
    disabledButton: {
      background: colors.border,
      color: colors.textLight,
      cursor: 'not-allowed',
    },
    voteSection: {
      padding: '1.5rem 2rem',
      background: colors.background,
      borderTop: `1px solid ${colors.border}`,
    },
    voteSectionLabel: {
      fontSize: '0.875rem',
      fontWeight: '500',
      color: colors.text,
      marginBottom: '0.75rem',
    },
    errorAlert: {
      padding: '0.875rem 1rem',
      background: '#fef2f2',
      color: colors.accent,
      borderRadius: '8px',
      marginBottom: '1.5rem',
      border: `1px solid #fecaca`,
      fontSize: '0.875rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
  };

  // Utilitaires
  const scrollToParagraph = (ref) => {
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const getUserRating = () => {
    const userVote = voteA?.find((vote) => vote.userId === userInfo._id);
    return userVote?.voteNum || 0;
  };

  // Handlers
  const handleChange = (fieldName) => (event) => {
    setValues({
      ...values,
      error: false,
      [fieldName]: event.target.value,
    });
  };

  const nameRef = useRef(null);
  const descriptionRef = useRef(null);

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!productId || values.loading) return;

    if (!(name?.length > 3)) {
      scrollToParagraph(nameRef);
      return;
    }
    if (!(description?.length > 8)) {
      scrollToParagraph(descriptionRef);
      return;
    }

    setValues({ ...values, error: false, loading: true });

    AddComments(token, productId, { name, description }).then((data) => {
      if (data.error) {
        setValues({ ...values, error: data.error, loading: false });
      } else {
        setValues({
          name: "",
          description: "",
          error: false,
          loading: false,
        });
        ButtonAddComments(false);
        setShowComments(false);
      }
    });
  };

  const loadMoreComments = (direction = "next") => {
    if (!productId) return;
    const toSkip = direction === "next" ? skip + limit : skip - limit;
    scrollToParagraph(divUserComment);

    commentProduct(productId, toSkip, limit).then((data) => {
      if (!data?.error) {
        setCommentsPro(data.commentsPagination);
        SetSizeComments(data.commentsCount?.[0]?.totalComments);
        setUserVote(data.userVote?.[0]?.vote);
        setSkip(toSkip);
      }
    });
  };

  // ROOT CAUSE (see the ticket's own report below): this form used to be a component FUNCTION
  // declared here, inside ProductPreview's render, and invoked as JSX (<CommentForm />). A function
  // defined inside a parent's render body is a NEW component type on every parent re-render --
  // React saw a "different" component each keystroke (setValues -> re-render -> a fresh
  // CommentForm reference) and remounted it: fresh DOM nodes, lost focus, and its own local
  // `focused` state reset every time. The fix is this file's usual convention already used by
  // renderHeroSlider/renderPageHeader-style helpers elsewhere in this codebase: compute the JSX as
  // a plain value in the SAME render, never invoke a locally-declared function as a JSX element.
  const isValid = name?.length > 3 && description?.length > 8;
  const errorAlert = error && (
    <div style={styles.errorAlert}>
      <span>⚠️</span>
      <span>{error}</span>
    </div>
  );
  const commentForm = addComment && (
      <div style={styles.formCard}>
        {/* Header */}
        <div style={styles.formHeader}>
          <h3 style={styles.formTitle}>
            {infoSource.laguangeElakay("title", infoSource.languageAPI).AddComments}
          </h3>
        </div>

        {/* Body */}
        <div style={styles.formBody}>
          {errorAlert}

          <form onSubmit={handleSubmit}>
            {/* Titre */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>
                {infoSource.laguangeElakay("title", infoSource.languageAPI).title}
              </label>
              <input
                ref={nameRef}
                type="text"
                value={name}
                onChange={handleChange("name")}
                placeholder={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).commentaireUserAddCommentsProduct.t001}
                onFocus={() => setFocused('name')}
                onBlur={() => setFocused(null)}
                style={{
                  ...styles.input,
                  borderColor: focused === 'name' ? colors.primary : colors.border,
                  boxShadow: focused === 'name' ? `0 0 0 3px ${colors.primary}15` : 'none',
                }}
              />
              <div style={styles.helperText}>
                {infoSource.laguangeElakay("text", infoSource.languageAPI).textCommentProduct1}
              </div>
            </div>

            {/* Description */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>
                {infoSource.laguangeElakay("sellPage", infoSource.languageAPI).description}
              </label>
              <textarea
                ref={descriptionRef}
                value={description}
                onChange={handleChange("description")}
                placeholder={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).commentaireUserAddCommentsProduct.t002}
                onFocus={() => setFocused('description')}
                onBlur={() => setFocused(null)}
                style={{
                  ...styles.textarea,
                  borderColor: focused === 'description' ? colors.primary : colors.border,
                  boxShadow: focused === 'description' ? `0 0 0 3px ${colors.primary}15` : 'none',
                }}
              />
              <div style={styles.helperText}>
                {infoSource.laguangeElakay("text", infoSource.languageAPI).textCommentProduct2}
              </div>
            </div>

            {/* Boutons */}
            <div style={styles.buttonGroup}>
              <button
                type="button"
                onClick={() => setShowComments(false)}
                style={{
                  ...styles.button,
                  ...styles.secondaryButton,
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = colors.background;
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = colors.white;
                }}
              >
                {infoSource.laguangeElakay("Button", infoSource.languageAPI).cancel}
              </button>

              <button
                type="submit"
                disabled={!isValid || !productId || values.loading}
                style={{
                  ...styles.button,
                  ...(isValid ? styles.primaryButton : styles.disabledButton),
                }}
                onMouseEnter={(e) => {
                  if (isValid) {
                    e.target.style.background = colors.primaryDark;
                  }
                }}
                onMouseLeave={(e) => {
                  if (isValid) {
                    e.target.style.background = colors.primary;
                  }
                }}
              >
                {infoSource.laguangeElakay("Button", infoSource.languageAPI).add}
              </button>
            </div>
          </form>
        </div>

        {/* Vote Section */}
        <div style={styles.voteSection}>
          <div style={styles.voteSectionLabel}>
            {infoSource.laguangeElakay("label", infoSource.languageAPI).vote}
          </div>
          <Vote
            ButtonAddComments={ButtonAddComments}
            productID={productId}
            token={token}
            rating={getUserRating()}
            clickShow={true}
            starSize="40px"
          />
        </div>
      </div>
  );

  return (
    <div style={styles.container}>
      <ViewVoteStarButton
        infoSource={infoSource}
        voteA={voteA}
        token={token}
        productId={productId}
        loadProductsView={loadProductsView}
        ButtonAddComments={ButtonAddComments}
        showComments={showComments}
        addComment={addComment}
        setAddComment={setAddComment}
      />

      <div ref={divUserComment}>
        {!addComment && (
          <UserComment
            infoSource={infoSource}
            skip={skip}
            limit={limit}
            setCommentsPro={setCommentsPro}
            siseProduct={sizeComments}
            commentProduct={commentProduct}
            SetSizeComments={SetSizeComments}
            setUserVote={setUserVote}
            comments={comments}
            voteA={voteA}
            productId={productId}
            token={token}
            loadMore={() => loadMoreComments("next")}
            loadMore0={() => loadMoreComments("prev")}
            ButtonProductPreview={() => setShowComments(false)}
          />
        )}
      </div>

      {commentForm}
    </div>
  );
};

export default ProductPreview;
